import React, {useState} from 'react';
import {
    Alert,
    AlertDescription, Icon,
} from "@chakra-ui/react";
import {Info} from 'lucide-react';

import objectUtil from "@/utils/objectUtil";

const validate = (rules, item) => {
    const errors = {};
    for (const key in rules) {
        const keyType = typeof item[key];
        if (
            rules[key]?.required?.value &&
            (!(key in item) ||
                (!['boolean', 'number'].includes(keyType) &&
                    (!item[key] || item[key].length === 0)) ||
                (keyType === 'number' && [undefined, null].includes(item[key])))
        ) {
            errors[key] = rules[key].required.message ?? 'This field is required!';
            continue;
        }
        if (rules[key]?.regex?.value && !rules[key].regex.value.test(item[key])) {
            errors[key] = rules[key].regex.message ?? 'Invalid value for this field!';
        }

        if (rules[key]?.arrayLength &&
            (
                !objectUtil.isArray(item[key]) ||
                (('min' in rules[key]?.arrayLength) && item[key].length < rules[key].arrayLength.min) ||
                (('max' in rules[key]?.arrayLength) && item[key].length > rules[key].arrayLength.max) ||
                (('exact' in rules[key]?.arrayLength) && item[key].length !== rules[key].arrayLength.exact)
            )
        ) {
            errors[key] = rules[key].arrayLength.message ?? 'Invalid value for this field!';
        }

        if (rules[key]?.custom?.function && !rules[key].custom.function(item[key])) {
            errors[key] = rules[key]?.custom?.message ?? rules[key]?.custom?.getMessage?.() ?? 'Invalid value for this field!';
        }
    }
    return {
        isValid: Object.keys(errors).length === 0,
        errors: errors,
    };
};

export const useForm = (initialState, initialValidationRules, hasUpload = false) => {
    const [formData, setFormData] = useState(initialState);
    const [validationRules, setValidationRules] = useState(initialValidationRules);
    const [formFieldErrors, setFormFieldErrors] = useState({});
    const [formError, setFormError] = useState(null);
    const [state, setState] = useState(1);

    const getFormErrors = () => {
        return formError !== null ? (
            <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Content>
                    <Alert.Title>There was an error processing your request</Alert.Title>
                    <Alert.Description>{Array.isArray(formError) ? formError.join('\n') : formError}</Alert.Description>
                </Alert.Content>
            </Alert.Root>
        ) : null;
    };

    /**
     * value can either be plain value, or callback function with prevData as a parameter
     * */
    const handleInputChange = (field, value) => {
        setFormData((prevData) => ({
            ...prevData,
            [field]: typeof value === 'function' ? value(prevData) : value,
        }));
    };

    const handleInputChanges = (values) => {
        setFormData((prevData) => ({
            ...prevData,
            ...values
        }));
    };

    const createFormData = (formData, parentKey = '', data = new FormData()) => {
        for (const key in formData) {
            if (formData.hasOwnProperty(key)) {
                const value = formData[key];
                const formKey = parentKey ? `${parentKey}[${key}]` : key;
                if (objectUtil.isArray(value)) {
                    if (value.length === 0) {
                        data.append(formKey, "[]");
                    }
                    value.forEach((item, index) => {
                        if (objectUtil.isArray(item) || (objectUtil.isObject(item) && !(item instanceof File) && !(item instanceof Date))) {
                            createFormData(item, `${formKey}[${index}]`, data);
                        } else {
                            data.append(`${formKey}[${index}]`, item);
                        }
                    });
                } else if (objectUtil.isObject(value) && !(value instanceof File) && !(value instanceof Date)) {
                    createFormData(value, formKey, data);
                } else if (![null, undefined].includes(value)) {
                    data.append(formKey, value);
                }
            }
        }
        return data;
    };

    const handleFormSubmit = async (formCallback, successCallback) => {
        setFormFieldErrors({});
        setFormError(null);
        if (state === 0) {
            return;
        }
        setState(0);
        const {isValid, errors} = validate(validationRules, formData);
        if (isValid) {
            try {
                let response;
                if (hasUpload) {
                    const data = createFormData(formData);
                    response = await formCallback(data);
                } else {
                    response = await formCallback(formData);
                }
                successCallback(response?.data?.value);
            } catch (error) {
                switch (error?.response?.data?.type) {
                    case 'form_field_error':
                        setFormFieldErrors(error?.response?.data?.value);
                        break;
                    case 'form_error':
                        setFormError(error?.response?.data?.value);
                        break;
                    default:
                        break;
                }
            }
        } else {
            setFormFieldErrors(errors);
        }
        setState(1);
    };

    return {
        formData,
        getFormErrors,
        formFieldErrors,
        state,
        handleInputChange,
        handleInputChanges,
        handleFormSubmit,
    };
};
